import { useMemo } from 'react';

import { ProductLine } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useExternalAccessRights, useVenuePosIntegrationStatus } from 'common/hooks';
import { FeatureType } from 'common/types';
import { usePermissions } from 'common/wolt-permissions';
import { hasCampaignEditPermission } from 'common/wolt-permissions/permissionUtils';
import { EntityType } from 'modules/access-control';

import useCampaignFeatureFlags from './useCampaignFeatureFlags';
import useDeliverySettings from './useDeliverySettings';
import { CampaignType } from '../../../apis/restaurant-api/merchant-admin/campaigns/types';
import { useIsAdCampaignEnabled } from '../ad-campaign/hooks/useIsAdCampaignsEnabled';

const useCampaignTypesPermittedToEdit = (values: {
  feature?: FeatureType;
  featureId: string;
  productLine?: ProductLine;
  city?: string;
  entityCountry?: Country | string;
}) => {
  const { feature, featureId, productLine, city, entityCountry } = values;
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const isCampaignEditPermissionEnabledForFeature = feature
    ? hasCampaignEditPermission(permissions, externalAccessRights, feature as EntityType, featureId)
    : false;
  const deliveryType = useDeliverySettings({ featureId, feature });
  const venueId = feature === 'venue' ? featureId : null;
  const { data: posIntegrationStatus } = useVenuePosIntegrationStatus(venueId);

  const { isEnabled: isAdCampaignEnabled } = useIsAdCampaignEnabled({
    country: entityCountry,
    productLine,
    venueId: featureId,
    city,
  });

  const { isLoading, campaignsFeatureFlags } = useCampaignFeatureFlags({
    feature,
    featureId,
    city,
    deliveryType,
    entityCountry,
    isPosEnabled: posIntegrationStatus?.is_pos_integrated,
  });

  const campaignTypesPermittedToEdit = useMemo((): CampaignType[] => {
    if (!isCampaignEditPermissionEnabledForFeature) {
      return [];
    }

    return (Object.keys(campaignsFeatureFlags) as (keyof typeof campaignsFeatureFlags)[]).reduce(
      (res, campaignFeatureFlag): CampaignType[] => {
        if (!campaignsFeatureFlags[campaignFeatureFlag]) {
          return res;
        }

        switch (campaignFeatureFlag) {
          case 'isDeliveryDiscountEditEnabled':
            return [...res, CampaignType.DELIVERY_DISCOUNT];
          case 'isBasketDiscountEditEnabled':
            return [...res, CampaignType.BASKET_DISCOUNT];
          case 'isAdsEditEnabled':
            return [...res]; // Ads use a different permission check
          case 'isItemDiscountEditEnabled':
            return [...res, CampaignType.ITEM_DISCOUNT];
          case 'isEndOfDayEditEnabled':
            return [...res, CampaignType.END_OF_DAY];
          default:
            const _exhaustiveCheck: never = campaignFeatureFlag;
            return _exhaustiveCheck;
        }
      },
      [] as CampaignType[],
    );
  }, [isCampaignEditPermissionEnabledForFeature, campaignsFeatureFlags]);

  if (isAdCampaignEnabled) {
    campaignTypesPermittedToEdit.push(CampaignType.ADS);
  }

  return {
    campaignTypesPermittedToEdit,
    isLoading,
  };
};

export default useCampaignTypesPermittedToEdit;
